/**
 * Agent Configuration Wizard
 * 
 * Dynamic wizard that adapts to each agent type:
 * - Hermes (Content Engine): Schedule, Content Preferences, Distribution
 * - Athena (Competitor Spy): Schedule, Your Website, Competitors to Track
 * - Distribution Machine: Schedule, Content Sources, Platforms
 */

import React, { useState } from 'react';

const AgentConfigWizard = ({ agent, API, onComplete, onCancel }) => {
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  
  const agentType = agent?.agent_type || 'content_engine';
  
  // Dynamic default name based on agent type
  const getDefaultName = () => {
    if (agentType === 'competitor_spy') return 'My Athena';
    if (agentType === 'distribution_machine') return 'My Distribution Machine';
    return 'My Content Engine';
  };
  const [agentName, setAgentName] = useState(getDefaultName());
  
  // Base config shared by all agents
  const [config, setConfig] = useState({
    schedule: {
      frequency: 'weekly',
      day_of_week: 'monday',
      time: '06:00',
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone || 'America/New_York'
    },
    // Hermes-specific
    content_preferences: {
      industry: '',
      topics: [],
      target_audience: '',
      tone: 'professional',
      word_count: 1500
    },
    distribution: {
      platforms: ['linkedin', 'twitter', 'instagram', 'facebook', 'tiktok'],
      email_delivery: true,
      save_to_dashboard: true,
      generate_hashtags: true,
      include_email_newsletter: true
    },
    features_enabled: {
      trending_topics: true,
      content_brief: true,
      content_generation: true,
      seo_optimization: true,
      social_variations: true,
      hashtag_generation: true,
      performance_prediction: true,
      ab_headlines: true
    },
    // Athena-specific - matches backend spy_config format
    spy_config: {
      website_url: '',
      competitors: [],
      industry: '',
      focus_areas: [],
      your_strengths: '',
      analysis_depth: 'standard'
    }
  });

  const [topicInput, setTopicInput] = useState('');
  const [keywordInput, setKeywordInput] = useState('');
  const [competitorInput, setCompetitorInput] = useState('');

  // Hermes: Add topic
  const handleAddTopic = () => {
    if (topicInput.trim() && config.content_preferences.topics.length < 10) {
      setConfig(prev => ({
        ...prev,
        content_preferences: {
          ...prev.content_preferences,
          topics: [...prev.content_preferences.topics, topicInput.trim()]
        }
      }));
      setTopicInput('');
    }
  };

  const handleRemoveTopic = (topic) => {
    setConfig(prev => ({
      ...prev,
      content_preferences: {
        ...prev.content_preferences,
        topics: prev.content_preferences.topics.filter(t => t !== topic)
      }
    }));
  };

  // Athena: Add keyword/focus area
  const handleAddKeyword = () => {
    if (keywordInput.trim() && config.spy_config.focus_areas.length < 10) {
      setConfig(prev => ({
        ...prev,
        spy_config: {
          ...prev.spy_config,
          focus_areas: [...prev.spy_config.focus_areas, keywordInput.trim()]
        }
      }));
      setKeywordInput('');
    }
  };

  const handleRemoveKeyword = (keyword) => {
    setConfig(prev => ({
      ...prev,
      spy_config: {
        ...prev.spy_config,
        focus_areas: prev.spy_config.focus_areas.filter(k => k !== keyword)
      }
    }));
  };

  // Athena: Add competitor
  const handleAddCompetitor = () => {
    if (competitorInput.trim() && config.spy_config.competitors.length < 5) {
      setConfig(prev => ({
        ...prev,
        spy_config: {
          ...prev.spy_config,
          competitors: [...prev.spy_config.competitors, competitorInput.trim()]
        }
      }));
      setCompetitorInput('');
    }
  };

  const handleRemoveCompetitor = (competitor) => {
    setConfig(prev => ({
      ...prev,
      spy_config: {
        ...prev.spy_config,
        competitors: prev.spy_config.competitors.filter(c => c !== competitor)
      }
    }));
  };

  const togglePlatform = (platform) => {
    setConfig(prev => {
      const platforms = prev.distribution.platforms.includes(platform)
        ? prev.distribution.platforms.filter(p => p !== platform)
        : [...prev.distribution.platforms, platform];
      return {
        ...prev,
        distribution: { ...prev.distribution, platforms }
      };
    });
  };

  const handleHire = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const token = localStorage.getItem('token');
      const agentTypeToUse = agent?.agent_type || 'content_engine';
      
      // First, check if user already has an agent of this type
      let existingAgentId = null;
      try {
        const checkResponse = await fetch(`${API}/agents`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (checkResponse.ok) {
          const checkData = await checkResponse.json();
          const existingAgent = checkData.agents?.find(a => a.agent_type === agentTypeToUse);
          if (existingAgent) {
            existingAgentId = existingAgent.id;
          }
        }
      } catch (e) {
        console.log('Could not check existing agents:', e);
      }
      
      let response;
      if (existingAgentId) {
        // UPDATE existing agent
        response = await fetch(`${API}/agents/${existingAgentId}`, {
          method: 'PUT',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            name: agentName,
            configuration: config
          })
        });
      } else {
        // CREATE new agent
        response = await fetch(`${API}/agents`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            agent_type: agentTypeToUse,
            name: agentName,
            configuration: config
          })
        });
      }

      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.detail || (existingAgentId ? 'Failed to update agent' : 'Failed to hire agent'));
      }

      onComplete(data.agent);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const canProceed = () => {
    switch (step) {
      case 1:
        return config.schedule.frequency && config.schedule.time;
      case 2:
        if (agentType === 'competitor_spy') {
          return config.spy_config.website_url.trim().length > 0;
        }
        if (agentType === 'distribution_machine') {
          return true; // Apollo just needs to know platforms to distribute to
        }
        return config.content_preferences.industry && config.content_preferences.target_audience;
      case 3:
        if (agentType === 'competitor_spy') {
          return true; // Competitors optional
        }
        return config.distribution.platforms.length > 0;
      case 4:
        return true;
      default:
        return false;
    }
  };

  // Get agent display name
  const getAgentDisplayName = () => {
    if (agent?.display_name) return agent.display_name;
    if (agentType === 'competitor_spy') return 'Athena';
    if (agentType === 'distribution_machine') return 'Apollo';
    return 'Hermes';
  };

  // Get header gradient based on agent type
  const getHeaderGradient = () => {
    if (agentType === 'competitor_spy') return 'from-amber-500 to-orange-600';
    if (agentType === 'distribution_machine') return 'from-yellow-500 to-orange-500';
    return 'from-purple-600 to-blue-600';
  };

  return (
    <div className="bg-white rounded-2xl shadow-xl max-w-3xl mx-auto overflow-hidden">
      {/* Header */}
      <div className={`bg-gradient-to-r ${getHeaderGradient()} p-6 text-white`}>
        <div className="flex items-center gap-3 mb-2">
          <span className="text-3xl">{agent?.emoji || '🤖'}</span>
          <h2 className="text-2xl font-bold">Configure {getAgentDisplayName()}</h2>
        </div>
        <p className="text-white/80">Step {step} of 4</p>
        
        {/* Progress Bar */}
        <div className="mt-4 bg-white/20 rounded-full h-2">
          <div 
            className="bg-white rounded-full h-2 transition-all duration-300"
            style={{ width: `${(step / 4) * 100}%` }}
          />
        </div>
      </div>

      {/* Content */}
      <div className="p-6">
        {error && (
          <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg text-red-700">
            {error}
          </div>
        )}

        {/* Step 1: Schedule */}
        {step === 1 && (
          <div className="space-y-6">
            <div>
              <h3 className="text-xl font-semibold text-gray-800 mb-4">📅 When should {getAgentDisplayName()} run?</h3>
              
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Frequency</label>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                    {['daily', 'weekly', 'biweekly', 'monthly'].map(freq => (
                      <button
                        key={freq}
                        onClick={() => setConfig(prev => ({
                          ...prev,
                          schedule: { ...prev.schedule, frequency: freq }
                        }))}
                        className={`p-3 rounded-xl border-2 transition capitalize ${
                          config.schedule.frequency === freq
                            ? 'border-purple-500 bg-purple-50 text-purple-700'
                            : 'border-gray-200 hover:border-gray-300'
                        }`}
                      >
                        {freq}
                        {freq === 'weekly' && <span className="block text-xs text-purple-500">Recommended</span>}
                      </button>
                    ))}
                  </div>
                </div>

                {(config.schedule.frequency === 'weekly' || config.schedule.frequency === 'biweekly') && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Day of Week</label>
                    <select
                      value={config.schedule.day_of_week}
                      onChange={e => setConfig(prev => ({
                        ...prev,
                        schedule: { ...prev.schedule, day_of_week: e.target.value }
                      }))}
                      className="w-full p-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                    >
                      {['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'].map(day => (
                        <option key={day} value={day} className="capitalize">{day.charAt(0).toUpperCase() + day.slice(1)}</option>
                      ))}
                    </select>
                  </div>
                )}

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Time (24-hour)</label>
                  <select
                    value={config.schedule.time}
                    onChange={e => setConfig(prev => ({
                      ...prev,
                      schedule: { ...prev.schedule, time: e.target.value }
                    }))}
                    className="w-full p-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  >
                    {Array.from({ length: 24 }, (_, i) => {
                      const hour = i.toString().padStart(2, '0');
                      return <option key={hour} value={`${hour}:00`}>{hour}:00</option>;
                    })}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Timezone</label>
                  <input
                    type="text"
                    value={config.schedule.timezone}
                    onChange={e => setConfig(prev => ({
                      ...prev,
                      schedule: { ...prev.schedule, timezone: e.target.value }
                    }))}
                    className="w-full p-3 border border-gray-300 rounded-xl bg-gray-50"
                    readOnly
                  />
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Step 2: Agent-specific configuration */}
        {step === 2 && agentType === 'competitor_spy' && (
          <div className="space-y-6">
            <h3 className="text-xl font-semibold text-gray-800 mb-4">🌐 Tell Athena About Your Business</h3>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Your Website URL *</label>
              <input
                type="url"
                value={config.spy_config.website_url}
                onChange={e => setConfig(prev => ({
                  ...prev,
                  spy_config: { ...prev.spy_config, website_url: e.target.value }
                }))}
                placeholder="https://yourcompany.com"
                className="w-full p-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-amber-500 focus:border-transparent"
              />
              <p className="text-sm text-gray-500 mt-1">Athena will analyze your site to understand your niche</p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Your Industry *</label>
              <input
                type="text"
                value={config.spy_config.industry}
                onChange={e => setConfig(prev => ({
                  ...prev,
                  spy_config: { ...prev.spy_config, industry: e.target.value }
                }))}
                placeholder="e.g., SaaS, E-commerce, Marketing Agency"
                className="w-full p-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-amber-500 focus:border-transparent"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Focus Areas (up to 10)</label>
              <div className="flex gap-2 mb-2">
                <input
                  type="text"
                  value={keywordInput}
                  onChange={e => setKeywordInput(e.target.value)}
                  onKeyPress={e => e.key === 'Enter' && handleAddKeyword()}
                  placeholder="e.g., AI marketing, content automation"
                  className="flex-1 p-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-amber-500 focus:border-transparent"
                />
                <button
                  onClick={handleAddKeyword}
                  className="px-4 py-2 bg-amber-500 text-white rounded-xl hover:bg-amber-600"
                >
                  Add
                </button>
              </div>
              <div className="flex flex-wrap gap-2">
                {config.spy_config.focus_areas.map(kw => (
                  <span 
                    key={kw}
                    className="inline-flex items-center gap-1 px-3 py-1 bg-amber-100 text-amber-700 rounded-full"
                  >
                    {kw}
                    <button onClick={() => handleRemoveKeyword(kw)} className="hover:text-amber-900">×</button>
                  </span>
                ))}
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Your Strengths (Optional)</label>
              <textarea
                value={config.spy_config.your_strengths}
                onChange={e => setConfig(prev => ({
                  ...prev,
                  spy_config: { ...prev.spy_config, your_strengths: e.target.value }
                }))}
                placeholder="What makes your business unique? What are you better at than competitors?"
                rows={3}
                className="w-full p-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-amber-500 focus:border-transparent"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Analysis Depth</label>
              <div className="grid grid-cols-2 gap-3">
                {[
                  { id: 'standard', name: 'Standard', desc: 'Quick competitor scan', credits: '~15 credits' },
                  { id: 'deep', name: 'Deep Analysis', desc: 'Full website crawl', credits: '~30 credits' }
                ].map(option => (
                  <button
                    key={option.id}
                    onClick={() => setConfig(prev => ({
                      ...prev,
                      spy_config: { ...prev.spy_config, analysis_depth: option.id }
                    }))}
                    className={`p-4 rounded-xl border-2 transition text-left ${
                      config.spy_config.analysis_depth === option.id
                        ? 'border-amber-500 bg-amber-50'
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    <p className="font-medium text-gray-800">{option.name}</p>
                    <p className="text-sm text-gray-500">{option.desc}</p>
                    <p className="text-xs text-amber-600 mt-1">{option.credits}</p>
                  </button>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Step 2: Hermes Content Preferences */}
        {step === 2 && agentType === 'content_engine' && (
          <div className="space-y-6">
            <h3 className="text-xl font-semibold text-gray-800 mb-4">🎯 What should {getAgentDisplayName()} focus on?</h3>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Your Industry *</label>
              <input
                type="text"
                value={config.content_preferences.industry}
                onChange={e => setConfig(prev => ({
                  ...prev,
                  content_preferences: { ...prev.content_preferences, industry: e.target.value }
                }))}
                placeholder="e.g., Technology, Marketing, Finance, Healthcare"
                className="w-full p-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-purple-500 focus:border-transparent"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Topics (up to 10)</label>
              <div className="flex gap-2 mb-2">
                <input
                  type="text"
                  value={topicInput}
                  onChange={e => setTopicInput(e.target.value)}
                  onKeyPress={e => e.key === 'Enter' && handleAddTopic()}
                  placeholder="Add a topic and press Enter"
                  className="flex-1 p-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                />
                <button
                  onClick={handleAddTopic}
                  className="px-4 py-2 bg-purple-600 text-white rounded-xl hover:bg-purple-700"
                >
                  Add
                </button>
              </div>
              <div className="flex flex-wrap gap-2">
                {config.content_preferences.topics.map(topic => (
                  <span 
                    key={topic}
                    className="inline-flex items-center gap-1 px-3 py-1 bg-purple-100 text-purple-700 rounded-full"
                  >
                    {topic}
                    <button onClick={() => handleRemoveTopic(topic)} className="hover:text-purple-900">×</button>
                  </span>
                ))}
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Target Audience *</label>
              <input
                type="text"
                value={config.content_preferences.target_audience}
                onChange={e => setConfig(prev => ({
                  ...prev,
                  content_preferences: { ...prev.content_preferences, target_audience: e.target.value }
                }))}
                placeholder="e.g., Tech professionals, CTOs, Small business owners"
                className="w-full p-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-purple-500 focus:border-transparent"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Writing Tone</label>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                {['professional', 'casual', 'enthusiastic', 'educational', 'persuasive', 'friendly'].map(tone => (
                  <button
                    key={tone}
                    onClick={() => setConfig(prev => ({
                      ...prev,
                      content_preferences: { ...prev.content_preferences, tone }
                    }))}
                    className={`p-3 rounded-xl border-2 transition capitalize ${
                      config.content_preferences.tone === tone
                        ? 'border-purple-500 bg-purple-50 text-purple-700'
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    {tone}
                  </button>
                ))}
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Blog Post Length: {config.content_preferences.word_count} words
              </label>
              <input
                type="range"
                min="500"
                max="3000"
                step="100"
                value={config.content_preferences.word_count}
                onChange={e => setConfig(prev => ({
                  ...prev,
                  content_preferences: { ...prev.content_preferences, word_count: parseInt(e.target.value) }
                }))}
                className="w-full"
              />
              <div className="flex justify-between text-sm text-gray-500">
                <span>500</span>
                <span>1500 (Recommended)</span>
                <span>3000</span>
              </div>
            </div>
          </div>
        )}

        {/* Step 2: Apollo - Distribution Configuration */}
        {step === 2 && agentType === 'distribution_machine' && (
          <div className="space-y-6">
            <h3 className="text-xl font-semibold text-gray-800 mb-4">☀️ Configure Apollo Distribution</h3>
            <p className="text-gray-600 mb-4">
              Apollo takes content from Hermes and distributes it across your chosen platforms with platform-optimized versions.
            </p>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-3">Distribution Platforms</label>
              <div className="grid grid-cols-2 gap-3">
                {[
                  { id: 'linkedin', name: 'LinkedIn', emoji: '💼', desc: 'Professional posts' },
                  { id: 'twitter', name: 'Twitter/X', emoji: '🐦', desc: 'Threads & tweets' },
                  { id: 'facebook', name: 'Facebook', emoji: '📘', desc: 'Engaging posts' },
                  { id: 'instagram', name: 'Instagram', emoji: '📸', desc: 'Captions & carousels' },
                  { id: 'tiktok', name: 'TikTok', emoji: '🎵', desc: 'Short-form scripts' },
                  { id: 'medium', name: 'Medium', emoji: '📝', desc: 'Long-form articles' }
                ].map(platform => (
                  <button
                    key={platform.id}
                    onClick={() => togglePlatform(platform.id)}
                    className={`p-4 rounded-xl border-2 transition text-left ${
                      config.distribution.platforms.includes(platform.id)
                        ? 'border-yellow-500 bg-yellow-50'
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    <div className="flex items-center gap-2">
                      <span className="text-xl">{platform.emoji}</span>
                      <span className="font-medium text-gray-800">{platform.name}</span>
                    </div>
                    <p className="text-sm text-gray-500 mt-1">{platform.desc}</p>
                  </button>
                ))}
              </div>
            </div>

            <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-4">
              <h4 className="font-medium text-yellow-800 mb-2">🌟 Apollo Superpowers</h4>
              <ul className="text-sm text-yellow-700 space-y-1">
                <li>✓ Automatically optimizes content for each platform</li>
                <li>✓ Generates platform-specific hashtags</li>
                <li>✓ Creates Twitter threads from long-form content</li>
                <li>✓ Suggests optimal posting times</li>
                <li>✓ Predicts engagement performance</li>
              </ul>
            </div>
          </div>
        )}

        {/* Step 3: Athena - Competitors to Track */}
        {step === 3 && agentType === 'competitor_spy' && (
          <div className="space-y-6">
            <h3 className="text-xl font-semibold text-gray-800 mb-4">🎯 Competitors to Track (Optional)</h3>
            <p className="text-gray-600 mb-4">
              Already know some competitors? Add them here. Otherwise, Athena will automatically discover them based on your website.
            </p>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Competitor URLs (up to 5)</label>
              <div className="flex gap-2 mb-2">
                <input
                  type="url"
                  value={competitorInput}
                  onChange={e => setCompetitorInput(e.target.value)}
                  onKeyPress={e => e.key === 'Enter' && handleAddCompetitor()}
                  placeholder="https://competitor.com"
                  className="flex-1 p-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-amber-500 focus:border-transparent"
                />
                <button
                  onClick={handleAddCompetitor}
                  disabled={config.spy_config.competitors.length >= 5}
                  className="px-4 py-2 bg-amber-500 text-white rounded-xl hover:bg-amber-600 disabled:opacity-50"
                >
                  Add
                </button>
              </div>
              <div className="space-y-2">
                {config.spy_config.competitors.map(comp => (
                  <div 
                    key={comp}
                    className="flex items-center justify-between p-3 bg-amber-50 rounded-xl"
                  >
                    <span className="text-amber-800">{comp}</span>
                    <button onClick={() => handleRemoveCompetitor(comp)} className="text-amber-600 hover:text-amber-800">×</button>
                  </div>
                ))}
              </div>
              {config.spy_config.competitors.length === 0 && (
                <p className="text-sm text-gray-500 mt-2 italic">
                  No competitors added. Athena will auto-discover them for you.
                </p>
              )}
            </div>

            <div className="p-4 bg-amber-50 rounded-xl border border-amber-200">
              <p className="text-sm text-amber-800">
                <strong>💡 Pro Tip:</strong> Let Athena discover competitors first - she often finds ones you didn&apos;t know about!
              </p>
            </div>
          </div>
        )}

        {/* Step 3: Hermes - Distribution */}
        {step === 3 && agentType === 'content_engine' && (
          <div className="space-y-6">
            <h3 className="text-xl font-semibold text-gray-800 mb-4">📱 Which platforms do you need content for?</h3>
            
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
              {[
                { id: 'linkedin', name: 'LinkedIn', emoji: '💼' },
                { id: 'twitter', name: 'Twitter/X', emoji: '🐦' },
                { id: 'instagram', name: 'Instagram', emoji: '📸' },
                { id: 'facebook', name: 'Facebook', emoji: '📘' },
                { id: 'tiktok', name: 'TikTok', emoji: '🎵' },
              ].map(platform => (
                <button
                  key={platform.id}
                  onClick={() => togglePlatform(platform.id)}
                  className={`p-4 rounded-xl border-2 transition flex items-center gap-3 ${
                    config.distribution.platforms.includes(platform.id)
                      ? 'border-purple-500 bg-purple-50'
                      : 'border-gray-200 hover:border-gray-300'
                  }`}
                >
                  <span className="text-2xl">{platform.emoji}</span>
                  <span className="font-medium">{platform.name}</span>
                  {config.distribution.platforms.includes(platform.id) && (
                    <span className="ml-auto text-purple-600">✓</span>
                  )}
                </button>
              ))}
            </div>

            <div className="space-y-3 pt-4 border-t">
              <h4 className="font-medium text-gray-700">Additional Options</h4>
              
              {[
                { key: 'email_delivery', label: 'Email me when content is ready', emoji: '📧' },
                { key: 'include_email_newsletter', label: 'Include email newsletter version', emoji: '📰' },
                { key: 'generate_hashtags', label: 'Generate hashtag strategy', emoji: '#️⃣' },
                { key: 'save_to_dashboard', label: 'Save to dashboard', emoji: '💾' },
              ].map(option => (
                <label key={option.key} className="flex items-center gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={config.distribution[option.key]}
                    onChange={e => setConfig(prev => ({
                      ...prev,
                      distribution: { ...prev.distribution, [option.key]: e.target.checked }
                    }))}
                    className="w-5 h-5 text-purple-600 rounded"
                  />
                  <span>{option.emoji}</span>
                  <span>{option.label}</span>
                </label>
              ))}
            </div>
          </div>
        )}

        {/* Step 3: Apollo - Additional Options */}
        {step === 3 && agentType === 'distribution_machine' && (
          <div className="space-y-6">
            <h3 className="text-xl font-semibold text-gray-800 mb-4">⚙️ Distribution Options</h3>
            
            <div className="space-y-3">
              {[
                { key: 'email_delivery', label: 'Email me when distribution is ready', emoji: '📧' },
                { key: 'generate_hashtags', label: 'Generate platform-specific hashtags', emoji: '#️⃣' },
                { key: 'save_to_dashboard', label: 'Save to dashboard for review', emoji: '💾' },
              ].map(option => (
                <label key={option.key} className="flex items-center gap-3 cursor-pointer p-3 rounded-xl border border-gray-200 hover:bg-yellow-50 transition">
                  <input
                    type="checkbox"
                    checked={config.distribution[option.key]}
                    onChange={e => setConfig(prev => ({
                      ...prev,
                      distribution: { ...prev.distribution, [option.key]: e.target.checked }
                    }))}
                    className="w-5 h-5 text-yellow-600 rounded"
                  />
                  <span className="text-xl">{option.emoji}</span>
                  <span className="font-medium">{option.label}</span>
                </label>
              ))}
            </div>

            <div className="bg-gradient-to-r from-yellow-50 to-orange-50 border border-yellow-200 rounded-xl p-4">
              <h4 className="font-medium text-yellow-800 mb-3">☀️ Selected Platforms ({config.distribution.platforms.length})</h4>
              <div className="flex flex-wrap gap-2">
                {config.distribution.platforms.map(p => (
                  <span key={p} className="px-3 py-1 bg-yellow-100 text-yellow-800 rounded-full text-sm capitalize">
                    {p}
                  </span>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Step 4: Review */}
        {step === 4 && (
          <div className="space-y-6">
            <h3 className="text-xl font-semibold text-gray-800 mb-4">✅ Review & Activate</h3>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Give your agent a name</label>
              <input
                type="text"
                value={agentName}
                onChange={e => setAgentName(e.target.value)}
                placeholder={getDefaultName()}
                className={`w-full p-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-${agentType === 'competitor_spy' ? 'amber' : 'purple'}-500 focus:border-transparent`}
              />
            </div>

            {/* Hermes Review */}
            {agentType === 'content_engine' && (
              <div className="bg-gray-50 rounded-xl p-4 space-y-3">
                <div className="flex justify-between">
                  <span className="text-gray-600">📅 Schedule</span>
                  <span className="font-medium capitalize">
                    Every {config.schedule.frequency === 'weekly' ? config.schedule.day_of_week : config.schedule.frequency} at {config.schedule.time}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">🎯 Industry</span>
                  <span className="font-medium">{config.content_preferences.industry}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">👥 Audience</span>
                  <span className="font-medium">{config.content_preferences.target_audience}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">📝 Tone</span>
                  <span className="font-medium capitalize">{config.content_preferences.tone}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">📱 Platforms</span>
                  <span className="font-medium">{config.distribution.platforms.length} platforms</span>
                </div>
              </div>
            )}

            {/* Athena Review */}
            {agentType === 'competitor_spy' && (
              <div className="bg-amber-50 rounded-xl p-4 space-y-3">
                <div className="flex justify-between">
                  <span className="text-amber-700">📅 Schedule</span>
                  <span className="font-medium capitalize">
                    Every {config.schedule.frequency === 'weekly' ? config.schedule.day_of_week : config.schedule.frequency} at {config.schedule.time}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-amber-700">🌐 Your Website</span>
                  <span className="font-medium text-sm">{config.spy_config.website_url || 'Not set'}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-amber-700">🏢 Industry</span>
                  <span className="font-medium">{config.spy_config.industry || 'Not set'}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-amber-700">🔍 Analysis Depth</span>
                  <span className="font-medium capitalize">{config.spy_config.analysis_depth}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-amber-700">🎯 Focus Areas</span>
                  <span className="font-medium">{config.spy_config.focus_areas.length || 'Auto-detect'}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-amber-700">🏆 Competitors</span>
                  <span className="font-medium">{config.spy_config.competitors.length || 'Auto-discover'}</span>
                </div>
              </div>
            )}

            {/* Apollo Review */}
            {agentType === 'distribution_machine' && (
              <div className="bg-yellow-50 rounded-xl p-4 space-y-3">
                <div className="flex justify-between">
                  <span className="text-yellow-700">📅 Schedule</span>
                  <span className="font-medium capitalize">
                    Every {config.schedule.frequency === 'weekly' ? config.schedule.day_of_week : config.schedule.frequency} at {config.schedule.time}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-yellow-700">📱 Platforms</span>
                  <span className="font-medium">{config.distribution.platforms.length} platforms</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-yellow-700">📧 Email Delivery</span>
                  <span className="font-medium">{config.distribution.email_delivery ? 'Yes' : 'No'}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-yellow-700">#️⃣ Auto Hashtags</span>
                  <span className="font-medium">{config.distribution.generate_hashtags ? 'Yes' : 'No'}</span>
                </div>
              </div>
            )}

            <div className={`${agentType === 'competitor_spy' ? 'bg-amber-50' : agentType === 'distribution_machine' ? 'bg-yellow-50' : 'bg-purple-50'} rounded-xl p-4`}>
              <div className="flex items-center justify-between mb-2">
                <span className={`font-semibold ${agentType === 'competitor_spy' ? 'text-amber-700' : agentType === 'distribution_machine' ? 'text-yellow-700' : 'text-purple-700'}`}>Estimated Cost</span>
                <span className={`text-2xl font-bold ${agentType === 'competitor_spy' ? 'text-amber-700' : agentType === 'distribution_machine' ? 'text-yellow-700' : 'text-purple-700'}`}>
                  ~{agentType === 'competitor_spy' ? (config.spy_config.analysis_depth === 'deep' ? '30' : '15') : agentType === 'distribution_machine' ? '20' : '28'} credits/run
                </span>
              </div>
              <p className={`text-sm ${agentType === 'competitor_spy' ? 'text-amber-600' : agentType === 'distribution_machine' ? 'text-yellow-600' : 'text-purple-600'}`}>
                {agentType === 'competitor_spy' 
                  ? 'Time saved: 4-6 hours per analysis • Find hidden opportunities'
                  : agentType === 'distribution_machine'
                    ? 'Time saved: 3-4 hours per distribution • Reach all platforms instantly'
                    : 'Time saved: 8-10 hours per week • Value: $200-300 per package'}
              </p>
            </div>
          </div>
        )}
      </div>

      {/* Footer */}
      <div className="p-6 bg-gray-50 border-t flex justify-between">
        <button
          onClick={() => step > 1 ? setStep(step - 1) : onCancel()}
          className="px-6 py-3 text-gray-600 hover:text-gray-800 font-medium"
        >
          {step > 1 ? '← Back' : 'Cancel'}
        </button>
        
        <button
          onClick={() => step < 4 ? setStep(step + 1) : handleHire()}
          disabled={!canProceed() || loading}
          className={`px-6 py-3 rounded-xl font-semibold transition ${
            canProceed() && !loading
              ? `bg-gradient-to-r ${getHeaderGradient()} text-white hover:opacity-90`
              : 'bg-gray-200 text-gray-500 cursor-not-allowed'
          }`}
        >
          {loading ? (
            <span className="flex items-center gap-2">
              <span className="animate-spin">⏳</span> Hiring...
            </span>
          ) : step < 4 ? (
            'Next →'
          ) : (
            `${agent?.emoji || '🤖'} Hire ${getAgentDisplayName()}!`
          )}
        </button>
      </div>
    </div>
  );
};

export default AgentConfigWizard;
